import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, OnDestroy, Output, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ReviewAdjAutomationService } from './review-adj-automation.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  gridPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

const MODAL_TO_ACTION: Record<string, string> = {
  excludedpopup: 'exclude_all',
  excludedpopupcurrentyear: 'exclude_current',
  includepopup: 'include',
};

@Component({
    selector: 'gtw-review-adj-automation',
    templateUrl: './review-adj-automation.component.html',
    styleUrls: ['./review-adj-automation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ReviewAdjAutomationComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();

  filterParams: any = {};
  baseURLs: any = {};
  gridData: any = [];
  rowData: any = {};

  actionType = '';
  modalName = '';

  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to proceed?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...',
  };

  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  showConfirmDialog = true;
  sendpopupData: any[] = [];
  ObjectIDLIST: any[] = [];
  logged_in_user = '';

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private reviewAdjAutomationService: ReviewAdjAutomationService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);

    const gridPayload = gridPayloadFromActiveScreen(activeScreen);
    if (gridPayload && Array.isArray(gridPayload)) {
      this.gridData = gridPayload;
      this.rowData = gridPayload[0]?.data || gridPayload[0] || {};
    } else {
      this.rowData = gridPayload || {};
      this.gridData = gridPayload ? [gridPayload] : [];
    }

    const callbackName: string =
      activeScreen?.data?.modalName ||
      activeScreen?.data?.modal_name ||
      activeScreen?.data?.colData?.call_back ||
      '';
    this.modalName = callbackName;
    this.actionType = MODAL_TO_ACTION[callbackName] || '';
    this.updateModalConfig();

    this.cdr.detectChanges();
  }

  private resolveApiBase(): string {
    return this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || this.baseURLs?.base_url || '';
  }

  init(): void {
    if (this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0 &&
        this.globalService && Object.keys(this.globalService).length > 0 &&
        this.gridData && this.gridData.length > 0 &&
        this.resolveApiBase()) {
      this.initializeData();
    }
  }

  initializeData(): void {
    if (this.USER_SETTINGS?.user?.sso_id) {
      this.logged_in_user = this.USER_SETTINGS.user.sso_id;
    }
    this.updateModalConfig();
  }

  updateModalConfig(): void {
    let message = 'Are you sure you want to proceed?';

    if (this.actionType === 'exclude_all') {
      message = 'Are you sure you want to Exclude for All Years?';
    } else if (this.actionType === 'exclude_current') {
      message = 'Are you sure you want to Exclude for Current Year?';
    } else if (this.actionType === 'include') {
      message = 'Are you sure you want to Include?';
    }

    this.modalConfig = { ...this.modalConfig, message };
  }

  onConfirm(): void {
    if (this.actionType === 'exclude_all') {
      this.confirmExcludeAll();
    } else if (this.actionType === 'exclude_current') {
      this.confirmExcludeCurrent();
    } else if (this.actionType === 'include') {
      this.confirmInclude();
    }
  }

  onCancel(): void {
    this.showConfirmDialog = false;
    this.cdr.detectChanges();
    this.requestGridUpdate.emit('cancel');
  }

  confirmExcludeAll(): void {
    this.processGridData('ALL YEARS');
  }

  confirmExcludeCurrent(): void {
    this.processGridData('CURRENT YEAR');
  }

  confirmInclude(): void {
    this.processGridData('INCLUDE CURRENT YEAR');
  }

  processGridData(excludedYear: string): void {
    this.sendpopupData = [];
    this.ObjectIDLIST = [];

    if (this.gridData && Array.isArray(this.gridData) && this.gridData.length > 0) {
      for (let e = 0; e < this.gridData.length; e++) {
        const gridItem = this.gridData[e].data || this.gridData[e];
        const tmp_json: any = {
          TAX_YEAR: this.globalService?.tax_year || this.filterParams?.tax_year,
          SCENARIO: this.globalService?.scenario || this.filterParams?.scenario,
          JCD_KEY: this.globalService?.jcd_key || this.filterParams?.jcd_key,
          EXCLUDED_YEAR: excludedYear,
          COMBINATION_KEY: gridItem.COMBINATION_KEY,
          SCHDM_ID: gridItem.SCHDM_ID,
          SCHDM_USER_DESC: gridItem.SCHDM_USER_DESC,
          SCHDM_TYPE: gridItem.SCHDM_TYPE,
          METHOD_OF_CALC: gridItem.METHOD_OF_CALC,
          MULTIPLIER: gridItem.MULTIPLIER,
          AMOUNT: gridItem.AMOUNT,
          TRIGGER_ACCT_KEY: gridItem.TRIGGER_ACCT_KEY,
          TRIGGER_CHART_KEY: gridItem.TRIGGER_CHART_KEY,
          POSTING_ACCT_KEY: gridItem.POSTING_ACCT_KEY,
          POSTING_TAC_KEY: gridItem.POSTING_TAC_KEY,
          TBBS_ACCT_KEY: gridItem.TBBS_ACCT_KEY,
          COUNTER_PARTY: gridItem.COUNTER_PARTY,
          PROVISION_YN: gridItem.PROVISION_YN,
          TP_KEY: gridItem.TP_KEY,
        };

        this.sendpopupData.push(tmp_json);
        this.ObjectIDLIST.push({ OBJECT_ID: gridItem.OBJECT_ID });
      }
    }

    this.saveReviewAdj();
  }

  saveReviewAdj(): void {
    this.modalConfig = { ...this.modalConfig, loading: true };
    this.cdr.detectChanges();

    this.reviewAdjAutomationService.saveReviewAdj(
      this.resolveApiBase(),
      this.sendpopupData,
      this.sendpopupData,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.modalConfig = { ...this.modalConfig, loading: false };

        if (result.callSuccess !== '1') {
          this.gtwSnackbarService.openSnackBar(
            result.errorMessage || 'Error occurred while saving',
            'gtw-snackbar--red'
          );
        } else {
          this.gtwSnackbarService.openSnackBar(
            'Data has been saved successfully',
            'gtw-snackbar--green'
          );
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.modalConfig = { ...this.modalConfig, loading: false };
        console.error('Error saving review adj automation:', error);
        this.gtwSnackbarService.openSnackBar(
          'Error occurred while saving. Please try again.',
          'gtw-snackbar--red'
        );
        this.cdr.detectChanges();
      }
    );
  }
}
